import { PDFDocument, StandardFonts, rgb, type PDFImage } from 'pdf-lib';
import type { SessionDetail } from './api';
import { sessionFeeLine } from './sessionFee';
import { sessionWalletFxLine, walletFxReceiptLines } from './sessionWalletFx';
import { formatReportMoney } from '@evi/shared';

/**
 * Driver-facing session receipt PDF.
 *
 * Client-side render via pdf-lib (no native deps, runs in the
 * browser). All the receipt data is already on the SessionDetailPage
 * — this helper composes a single-page A4 PDF and triggers a blob
 * download. No server round-trip needed for the receipt itself
 * The session-specific receipt screen uses the same selected server row;
 * unused authorization refunds are not presented as charging invoices.
 *
 * Layout — A4 portrait (595×842 pt), generous margins for printability:
 *
 *   ── header band ──────────────────────────────────────
 *   EV INITIATIVE                          RECEIPT
 *   Powered by EVI                         {date}
 *   ── thin rule ─────────────────────────────────────────
 *
 *   SESSION
 *   {location name}
 *   {address}                              {charger identity}
 *
 *   DETAILS                                AMOUNT
 *   Energy delivered      {kWh}            Energy        $X
 *   Duration              {h m}            Idle / time   $X
 *   Started               {ts}             Connection fee (EVI)  $X
 *   Ended                 {ts}             Tax           $X
 *                                          ─────────────────
 *                                          Total         $X
 *
 *   Payment: {method line}                 Session ID: {id}
 *
 *   ── footer ────────────────────────────────────────────
 *   Questions? support@evinitiative.com
 *
 * Fleet sessions replace the "Payment" line with "Billed to:
 * {fleet_name}" so the driver knows the receipt reflects fleet-
 * sponsored billing rather than a personal-card charge.
 *
 * The fixed-fee row follows lib/sessionFee.ts: "Connection fee (EVI)"
 * on a session that pinned EVI's fee at start, "Session fee" on a
 * legacy one. English only, like the rest of the PDF.
 *
 * Home sessions (the `home` option, passed by surfaces that detected
 * the caller's own home charger via useHomeSession) drop the EVI cost
 * breakdown entirely — EVI never bills home charging — and show the
 * estimated utility cost at the owner's entered rate instead, framed
 * as what their utility bills them.
 */

/** Home-session framing passed by the calling surface. */
export interface ReceiptHomeFraming {
  /** Estimated utility cost in cents, or null when no usable rate. */
  estimateCents: number | null;
  /** "at <this>" phrase ("$0.15/kWh", "your time-of-use rates"). */
  rateSummary: string | null;
  /** The home's currency, which estimateCents is in (item 3): the
   *  owner's electricity rate is in it, not in the session's. Absent:
   *  the session's currency, as before. */
  currency?: string | null;
}

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN_X = 56;
const MARGIN_TOP = 64;

// Brand palette aligned with the PWA's --evi-accent fallback chain.
// Hardcoded here because the receipt prints outside the runtime
// theme system (CSS vars aren't applied to PDFs).
const COLOR_TEXT = rgb(0.12, 0.12, 0.12);
const COLOR_MUTED = rgb(0.45, 0.45, 0.45);
const COLOR_ACCENT = rgb(0.08, 0.50, 0.24); // EVI green, darkened (~Tailwind green-700 #15803D)
const COLOR_RULE = rgb(0.88, 0.88, 0.88);

function formatCentsForPdf(cents: number, currency: string): string {
  // Server's formatCents helper isn't reusable here — we render
  // synchronously into pdf-lib's draw calls. Same shape, fewer deps.
  return formatReportMoney(cents, currency);
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function durationLabel(minutes: number): string {
  // Round the total first, then split, so a fractional last minute never
  // renders as "1h 60m" (flooring the hour and rounding the remainder
  // independently could carry the minutes to 60).
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/**
 * Payment-method copy resolution. Fleet sessions ALWAYS surface the
 * fleet attribution rather than the underlying card so the receipt
 * matches the driver's mental model ("my employer paid"). Personal
 * sessions fall back to a generic "Card on file" copy because the
 * PWA doesn't surface the last4 on SessionDetail today — adding it
 * would require an extra payment-methods join at session-fetch time.
 */
function paymentMethodLine(session: SessionDetail, home?: ReceiptHomeFraming): string {
  if (!home && session.idle_view && !session.idle_view.cable_removed_at && !session.idle_view.billing_closed) return 'Payment not final. Idle fees may apply until you unplug.';
  if (!home && session.idle_view?.settlement_pending) return 'Payment pending. See the session for the hold and follow-on charge.';
  if (home) {
    // Never billed by EVI; the estimate (when present) is the utility's.
    return home.estimateCents != null && home.rateSummary
      ? `Utility estimate at ${home.rateSummary}. Not ledger reimbursement.`
      : 'Home charging - not billed by EVI.';
  }
  if (session.billed_to === 'fleet') {
    return `Billed to: ${session.fleet_name ?? 'your fleet'}`;
  }
  // A USD wallet that paid this CAD session (Warren, 2026-10-25T02:52:41Z,
  // option 1); the amounts and the rate print on their own lines above.
  if (sessionWalletFxLine(session)) return session.stripe_payment_intent_id ? 'Payment: USD wallet and card on file' : 'Payment: USD wallet';
  if (session.stripe_payment_intent_id) {
    return 'Payment: Card on file';
  }
  // Wallet-only / RFID-only / free sessions land here.
  return 'Payment: Wallet';
}

export async function buildSessionReceiptPdf(
  session: SessionDetail,
  home?: ReceiptHomeFraming,
): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  const sans = await doc.embedFont(StandardFonts.Helvetica);
  const sansBold = await doc.embedFont(StandardFonts.HelveticaBold);
  const mono = await doc.embedFont(StandardFonts.Courier);

  // Logo mark — embedded from the PWA public asset (same /evi-logo-mark.png
  // path the app shell uses). Optional: if the fetch or embed fails
  // (offline, asset moved), the header falls back to the wordmark-only
  // layout below. The PNG carries its own baked-in color and is drawn
  // as-is — NOT recolored to the accent.
  let logoImage: PDFImage | null = null;
  try {
    const logoRes = await fetch('/evi-logo-mark.png');
    if (logoRes.ok) {
      logoImage = await doc.embedPng(await logoRes.arrayBuffer());
    }
  } catch {
    logoImage = null;
  }

  const cur = session.cost?.currency ?? 'USD';
  const total = Math.round(session.idle_view?.total_cents ?? session.cost?.total ?? 0);
  const energy = Math.round(session.cost?.energy_cost ?? 0);
  const time = Math.round(session.idle_view?.idle_cents ?? session.cost?.time_cost ?? 0);
  const feeLine = sessionFeeLine(session);
  const tax = Math.round(session.idle_view?.tax_cents ?? session.cost?.tax ?? 0);

  // ── Header band ──────────────────────────────────────────────────
  let y = PAGE_HEIGHT - MARGIN_TOP;

  // Logo mark sits to the LEFT of the wordmark, spanning the two-line
  // header text block; the wordmark + subtitle shift right to make room.
  // Falls back to wordmark-only (textX = MARGIN_X) when the mark didn't
  // embed. The y arithmetic below is unchanged, so everything under the
  // header keeps its position.
  let textX = MARGIN_X;
  if (logoImage) {
    const LOGO_H = 30;
    const logoW = (logoImage.width / logoImage.height) * LOGO_H;
    page.drawImage(logoImage, {
      x: MARGIN_X,
      y: y - 17, // bottom edge — spans down level with the subtitle line
      width: logoW,
      height: LOGO_H,
    });
    textX = MARGIN_X + logoW + 12;
  }

  page.drawText('EV INITIATIVE', {
    x: textX, y, size: 16, font: sansBold, color: COLOR_ACCENT,
  });
  const receiptLabel = 'RECEIPT';
  const receiptLabelWidth = sansBold.widthOfTextAtSize(receiptLabel, 12);
  page.drawText(receiptLabel, {
    x: PAGE_WIDTH - MARGIN_X - receiptLabelWidth, y: y + 2,
    size: 12, font: sansBold, color: COLOR_MUTED,
  });

  y -= 16;
  page.drawText('Charging session receipt', {
    x: textX, y, size: 9, font: sans, color: COLOR_MUTED,
  });
  const dateLine = session.ended_at
    ? formatDateTime(session.ended_at)
    : formatDateTime(session.started_at);
  const dateLineWidth = sans.widthOfTextAtSize(dateLine, 9);
  page.drawText(dateLine, {
    x: PAGE_WIDTH - MARGIN_X - dateLineWidth, y,
    size: 9, font: sans, color: COLOR_MUTED,
  });

  y -= 16;
  page.drawLine({
    start: { x: MARGIN_X, y },
    end: { x: PAGE_WIDTH - MARGIN_X, y },
    thickness: 0.6,
    color: COLOR_RULE,
  });

  // ── Session location block ───────────────────────────────────────
  y -= 28;
  page.drawText('SESSION', {
    x: MARGIN_X, y, size: 9, font: sansBold, color: COLOR_MUTED,
  });
  y -= 18;
  page.drawText(session.location_name ?? 'Charging location', {
    x: MARGIN_X, y, size: 14, font: sansBold, color: COLOR_TEXT,
  });
  // Charger identity right-aligned on the same row as location name.
  const chargerLine = session.charger_identity ?? '';
  if (chargerLine) {
    const chargerWidth = mono.widthOfTextAtSize(chargerLine, 10);
    page.drawText(chargerLine, {
      x: PAGE_WIDTH - MARGIN_X - chargerWidth, y: y + 2,
      size: 10, font: mono, color: COLOR_MUTED,
    });
  }

  // ── Two-column details + cost breakdown ──────────────────────────
  y -= 36;
  const leftX = MARGIN_X;
  const rightX = PAGE_WIDTH / 2 + 20;

  page.drawText('DETAILS', {
    x: leftX, y, size: 9, font: sansBold, color: COLOR_MUTED,
  });
  page.drawText('AMOUNT', {
    x: rightX, y, size: 9, font: sansBold, color: COLOR_MUTED,
  });

  y -= 20;
  const rowSpacing = 18;

  const details: Array<[string, string]> = [
    ['Energy delivered', `${session.energy_kwh.toFixed(2)} kWh`],
    ['Duration', durationLabel(session.duration_minutes)],
    ['Started', formatDateTime(session.started_at)],
  ];
  if (session.ended_at) {
    details.push(['Ended', formatDateTime(session.ended_at)]);
  }

  // Home sessions never showcase an EVI cost breakdown — EVI bills
  // nothing at a home charger. One honest row; the accent line below
  // carries the utility estimate when the owner entered a rate.
  const breakdown: Array<[string, string]> = home
    ? [['Home charging', 'Free']]
    : [
        ['Energy', formatCentsForPdf(energy, cur)],
        ...receiptIdleRows(session, cur, time),
        ...(feeLine
          ? [[
              feeLine.kind === 'connection' ? 'Connection fee (EVI)' : 'Session fee',
              formatCentsForPdf(feeLine.cents, cur),
            ] as [string, string]]
          : []),
        ...(tax > 0 ? [['Tax', formatCentsForPdf(tax, cur)] as [string, string]] : []),
      ];

  if (!home && session.hold_summary) {
    const h = session.hold_summary;
    breakdown.push(['Original hold', formatCentsForPdf(h.original_hold_cents, h.currency)]);
    if (h.state === 'settled' && h.captured_cents !== null) breakdown.push(['Final charge', formatCentsForPdf(h.captured_cents, h.currency)]);
    else breakdown.push(['Settlement', h.state === 'failed' ? 'Payment failed' : 'Pending confirmation']);
    if (h.released_cents !== null) breakdown.push(['Unused hold released', formatCentsForPdf(h.released_cents, h.currency)]);
    if (h.refunded_cents) breakdown.push(['Refunded', formatCentsForPdf(h.refunded_cents, h.currency)]);
    details.push(['Hold', 'Not a charge'], ['Release timing', 'Your bank may take longer']);
  }

  // Render both columns in parallel; advance y by the LONGER column
  // so the two halves stay aligned. Currently details is always >=
  // breakdown (4 rows vs ~3-4), but iterate defensively.
  const rowCount = Math.max(details.length, breakdown.length);
  for (let i = 0; i < rowCount; i++) {
    const yi = y - i * rowSpacing;
    const d = details[i];
    if (d) {
      page.drawText(d[0], { x: leftX, y: yi, size: 10, font: sans, color: COLOR_MUTED });
      page.drawText(d[1], { x: leftX + 110, y: yi, size: 10, font: sansBold, color: COLOR_TEXT });
    }
    const b = breakdown[i];
    if (b) {
      page.drawText(b[0], { x: rightX, y: yi, size: 10, font: sans, color: COLOR_MUTED });
      const amtWidth = sansBold.widthOfTextAtSize(b[1], 10);
      page.drawText(b[1], {
        x: PAGE_WIDTH - MARGIN_X - amtWidth, y: yi,
        size: 10, font: sansBold, color: COLOR_TEXT,
      });
    }
  }

  y -= (rowCount - 1) * rowSpacing + rowSpacing;
  // Total row — rule above + accent-colored amount. Home sessions show
  // the utility ESTIMATE here (their utility's number, not ours) and
  // skip the line entirely when no rate is on file.
  if (!home || home.estimateCents != null) {
    page.drawLine({
      start: { x: rightX, y: y + 8 },
      end: { x: PAGE_WIDTH - MARGIN_X, y: y + 8 },
      thickness: 0.5,
      color: COLOR_RULE,
    });
    const totalLabel = home ? 'Est. utility cost' : session.idle_view && !session.idle_view.cable_removed_at && !session.idle_view.billing_closed ? 'Total so far' : 'Total';
    page.drawText(totalLabel, {
      x: rightX, y: y - 6, size: 11, font: sansBold, color: COLOR_TEXT,
    });
    const totalStr = home
      ? formatCentsForPdf(Math.round(home.estimateCents ?? 0), home.currency ?? cur)
      : formatCentsForPdf(total, cur);
    const totalWidth = sansBold.widthOfTextAtSize(totalStr, 12);
    page.drawText(totalStr, {
      x: PAGE_WIDTH - MARGIN_X - totalWidth, y: y - 6,
      size: 12, font: sansBold, color: COLOR_ACCENT,
    });
  }

  // ── Payment + session id row ─────────────────────────────────────
  y -= 38;
  if (!home && session.idle_view) {
    const v = session.idle_view;
    const lines = [
      `Idle: ${formatCentsForPdf(v.rate_cents, cur)}/min after ${v.grace_minutes} min. Maximum ${formatCentsForPdf(v.max_charge_cents, cur)}${v.host_maximum ? ' (host maximum)' : ' (2 hours)'}.`,
      ...(v.captured_from_hold_cents != null ? [`Captured from hold: ${formatCentsForPdf(v.captured_from_hold_cents, cur)}`] : []),
      ...(v.follow_on_cents != null ? [`Follow-on charge: ${formatCentsForPdf(v.follow_on_cents, cur)} (${v.follow_on_status})`] : []),
    ];
    for (const line of lines) { page.drawText(line, { x: MARGIN_X, y, size: 9, font: sansBold, color: COLOR_TEXT }); y -= 18; }
  }
  // The converted wallet payment and its pinned Bank of Canada rate.
  const walletFx = home ? null : sessionWalletFxLine(session);
  if (walletFx) {
    for (const line of walletFxReceiptLines(walletFx, formatCentsForPdf)) {
      page.drawText(line, { x: MARGIN_X, y, size: 9, font: sans, color: COLOR_TEXT }); y -= 14;
    }
    y -= 4;
  }
  page.drawText(paymentMethodLine(session, home), {
    x: MARGIN_X, y, size: 10, font: sans, color: COLOR_TEXT,
  });
  const sidLabel = `Session ID: ${String(session._id ?? '')}`;
  const sidWidth = mono.widthOfTextAtSize(sidLabel, 9);
  page.drawText(sidLabel, {
    x: PAGE_WIDTH - MARGIN_X - sidWidth, y,
    size: 9, font: mono, color: COLOR_MUTED,
  });

  // ── Footer ───────────────────────────────────────────────────────
  const footerY = 48;
  page.drawLine({
    start: { x: MARGIN_X, y: footerY + 18 },
    end: { x: PAGE_WIDTH - MARGIN_X, y: footerY + 18 },
    thickness: 0.4,
    color: COLOR_RULE,
  });
  page.drawText('Questions? support@evinitiative.com', {
    x: MARGIN_X, y: footerY, size: 9, font: sans, color: COLOR_MUTED,
  });

  // ── Serialize + trigger download ─────────────────────────────────
  // pdf-lib returns a Uint8Array<ArrayBufferLike>; the Blob ctor
  // wants a Uint8Array<ArrayBuffer>. Wrap in a fresh Uint8Array
  // copy so the type narrows without runtime overhead worth
  // worrying about (PDFs are small).
  return doc.save();
}

/** PDF read model only: the billed minutes and policy accompany the idle amount. */
export function receiptIdleRows(session: SessionDetail, currency: string, time: number): Array<[string, string]> {
  const policy = session.idle_policy_at_start;
  const view = session.idle_view;
  if (!policy?.rate_cents && !view?.rate_cents) return time > 0 ? [['Idle / time', formatCentsForPdf(time, currency)]] : [];
  const rate = view?.rate_cents ?? policy!.rate_cents;
  const grace = view?.grace_minutes ?? policy!.grace_minutes;
  const capMinutes = view?.billed_cap_minutes ?? policy!.billed_cap_minutes;
  const max = view?.max_charge_cents ?? Math.min(rate * capMinutes, policy?.max_charge_cents ?? Infinity);
  const billed = session.idle_report?.billed_idle_minutes ?? view?.billable_minutes;
  return [
    ['Idle fee', formatCentsForPdf(time, currency)],
    ['Billed idle', billed == null ? 'Not recorded' : `${billed} min`],
    ['Idle rate', `${formatCentsForPdf(rate, currency)}/min`],
    ['Idle grace', `${grace} min`],
    ['Idle maximum', formatCentsForPdf(max, currency)],
    ['Billed-time limit', `${capMinutes} min`],
  ];
}

export async function downloadSessionReceipt(session: SessionDetail, home?: ReceiptHomeFraming): Promise<void> {
  const hold_summary = session.pre_auth_payment_intent_id && !home ? await import('./api').then(api => api.fetchSessionHold(String(session._id))).catch(() => ({
    currency: session.cost.currency, original_hold_cents: session.pre_auth_amount_cents ?? 0,
    state: 'pending' as const, captured_cents: null, released_cents: null, refunded_cents: null, checked_at: new Date().toISOString(),
  })) : null;
  const bytes = await buildSessionReceiptPdf({ ...session, hold_summary }, home);
  const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const dateStr = (session.ended_at ?? session.started_at).slice(0, 10);
  const filename = `evi-receipt-${String(session._id ?? 'session')}-${dateStr}.pdf`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revocation a tick so Safari finishes the download before
  // the blob URL goes away. Same pattern admin/qr-pdf uses.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
